import { createContext, useContext, useEffect, useState, useRef, useMemo, Fragment, type ReactNode } from 'react';
import { loginOutputSchema, loginSuccessSchema, type AuthUser } from '@1financial/shared';
import { createClient, bindIdentityClient, type ApiClient } from '@1financial/api-client';
import { clearPrivateCache, queryClient } from './query-client.js';

const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/v1';
const REFRESH_KEY = 'ops.refresh_token';
interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  mfaPending: string | null;
  authSignal: number;
  privacyEpoch: number;
  login: (email: string, password: string) => Promise<void>;
  completeMfa: (code: string) => Promise<void>;
  cancelMfa: () => void;
  logout: () => Promise<void>;
  api: ApiClient | null;
  refreshCapabilities: () => Promise<Set<string>>;
}
const AuthContext = createContext<AuthState | null>(null);
function getStoredRefresh(): string | null {
  try { return localStorage.getItem(REFRESH_KEY); } catch { return null; }
}
function setStoredRefresh(token: string | null): void {
  try { if (token) localStorage.setItem(REFRESH_KEY, token); else localStorage.removeItem(REFRESH_KEY); } catch {}
}
let accessToken: string | null = null;
let onTerminated = () => {};
let onRefreshed = () => {};
export function getAccessToken(): string | null { return accessToken; }
const client = createClient({
  baseUrl: API_BASE_URL, getAccessToken, getRefreshToken: getStoredRefresh,
  setTokens: (tokens) => { accessToken = tokens.accessToken; setStoredRefresh(tokens.refreshToken); },
  clearTokens: () => { accessToken = null; setStoredRefresh(null); },
  onSessionTerminated: () => { accessToken = null; setStoredRefresh(null); clearPrivateCache(); onTerminated(); },
  onRefreshed: () => onRefreshed(),
});
export const authenticatedFetch = (path: string, init?: RequestInit) => client.authenticatedFetch(path, init);
export const captureAuthIdentity = () => {
  const generation = client.generation;
  return () => client.isCurrent(generation);
};
export const captureAuthenticatedFetch = () => {
  const bound = bindIdentityClient(client);
  return (path: string, init?: RequestInit) => bound.authenticatedFetch(path, init);
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const currentUser = useRef<AuthUser | null>(null);
  const permissions = useRef<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaPending, setMfaPending] = useState<string | null>(null);
  const [authSignal, setAuthSignal] = useState(0);
  const [privacyEpoch, setPrivacyEpoch] = useState(0);
  const clearViews = () => {
    clearPrivateCache();
    setPrivacyEpoch((n) => n + 1);
  };
  const invalidateIdentity = () => {
    client.resetIdentity();
    accessToken = null;
    setStoredRefresh(null);
    clearViews();
    currentUser.current = null;
    permissions.current = null;
    setUser(null);
    setMfaPending(null);
    return client.generation;
  };
  const authRevision = useRef(0);
  const synchronization = useRef<{ generation: number; promise: Promise<void> } | null>(null);
  const synchronize = (): Promise<void> => {
    if (synchronization.current?.generation === client.generation) return synchronization.current.promise;
    const flight = { generation: client.generation, promise: Promise.resolve() };
    synchronization.current = flight;
    flight.promise = (async () => {
      let g = flight.generation;
      const response = await authenticatedFetch(`${API_BASE_URL}/auth/session`);
      if (!client.isCurrent(g)) return;
      if (response.status === 401 || response.status === 403) { await client.clearSession(); return; }
      if (!response.ok) throw new Error('Session is temporarily unavailable');
      const body = await response.json() as { ok: boolean; data?: { user: AuthUser } };
      if (!client.isCurrent(g)) return;
      if (!body.ok || !body.data?.user) throw new Error('Invalid session response');
      const nextUser = body.data.user;
      if (currentUser.current && (currentUser.current.id !== nextUser.id || currentUser.current.role !== nextUser.role)) {
        g = client.resetIdentity();
        flight.generation = g;
        clearViews();
        currentUser.current = null;
        permissions.current = null;
        setUser(null);
      }
      const permissionResponse = await authenticatedFetch(`${API_BASE_URL}/permissions/mine`);
      if (!client.isCurrent(g)) return;
      if (permissionResponse.status === 401 || permissionResponse.status === 403) { await client.clearSession(); return; }
      if (!permissionResponse.ok) throw new Error('Permissions are temporarily unavailable');
      const permissionBody = await permissionResponse.json() as { ok: boolean; data?: { items: string[] } };
      if (!client.isCurrent(g)) return;
      if (!permissionBody.ok || !Array.isArray(permissionBody.data?.items) || permissionBody.data.items.some((item) => typeof item !== 'string')) throw new Error('Invalid permissions response');
      const items = [...new Set(permissionBody.data.items)].sort();
      const fingerprint = JSON.stringify(items);
      const changed = permissions.current !== fingerprint;
      if (permissions.current !== null && changed) {
        client.resetIdentity();
        clearViews();
      }
      if (changed) authRevision.current++;
      const permissionKey = ['my-permissions', nextUser.id, nextUser.role, authRevision.current];
      if (changed || !queryClient.getQueryData(permissionKey)) queryClient.setQueryData(permissionKey, new Set(items));
      if (changed) setAuthSignal(authRevision.current);
      permissions.current = fingerprint;
      const previous = currentUser.current;
      if (!previous || Object.keys(nextUser).some((key) => previous[key as keyof AuthUser] !== nextUser[key as keyof AuthUser])) {
        currentUser.current = nextUser;
        setUser(nextUser);
      }
    })().finally(() => { if (synchronization.current === flight) synchronization.current = null; });
    return flight.promise;
  };
  useEffect(() => {
    let disposed = false;
    onTerminated = () => {
      currentUser.current = null;
      permissions.current = null;
      setUser(null);
      setMfaPending(null);
      setPrivacyEpoch((n) => n + 1);
    };
    onRefreshed = () => { if (!disposed && currentUser.current) void synchronize().catch(() => {}); };
    const restore = async () => {
      if (!getStoredRefresh()) { if (!disposed) setLoading(false); return; }
      try { if (await client.refreshSession()) { if (!disposed) await synchronize(); } }
      catch {}
      finally { if (!disposed) setLoading(false); }
    };
    void restore();
    const foreground = () => { if (document.visibilityState !== 'hidden') void (currentUser.current ? synchronize() : restore()).catch(() => {}); };
    window.addEventListener('focus', foreground);
    document.addEventListener('visibilitychange', foreground);
    const timer = setInterval(() => {
      if (getStoredRefresh()) void client.refreshSession().then((ok) => { if (ok && !currentUser.current) return synchronize(); }).catch(() => {});
    }, 10 * 60_000);
    const sessionTimer = setInterval(() => { if (currentUser.current && document.visibilityState !== 'hidden') void synchronize().catch(() => {}); }, 30_000);
    return () => {
      disposed = true;
      onTerminated = () => {};
      onRefreshed = () => {};
      window.removeEventListener('focus', foreground);
      document.removeEventListener('visibilitychange', foreground);
      clearInterval(timer);
      clearInterval(sessionTimer);
    };
  }, []);

  const submitAuth = async (path: string, payload: unknown, generation: number) => {
    const response = await fetch(`${API_BASE_URL}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    const body = await response.json() as { ok: boolean; data?: unknown; error?: { message: string } };
    if (!client.isCurrent(generation)) throw new Error('Sign-in cancelled');
    if (!response.ok || !body.ok) throw new Error(body.error?.message ?? 'Sign-in failed');
    return loginOutputSchema.parse(body.data);
  };
  const commit = async (data: unknown, generation: number) => {
    const full = loginSuccessSchema.parse(data);
    if (!client.isCurrent(generation)) throw new Error('Sign-in cancelled');
    if (!(await client.setSession(full))) throw new Error('Sign-in cancelled');
    setMfaPending(null);
    await synchronize();
  };
  const login = async (email: string, password: string) => {
    const g = invalidateIdentity();
    const data = await submitAuth('/auth/login', { email, password }, g);
    if (data.mfaRequired) { setMfaPending(data.mfaToken); return; }
    await commit(data, g);
  };
  const completeMfa = async (code: string) => {
    if (!mfaPending) throw new Error('No MFA challenge in progress');
    const g = client.generation;
    const data = await submitAuth('/auth/login/mfa', { mfaToken: mfaPending, code: code.trim() }, g);
    await commit(data, g);
  };
  const cancelMfa = () => { invalidateIdentity(); };
  const logout = async () => {
    const token = accessToken;
    invalidateIdentity();
    try { await fetch(`${API_BASE_URL}/auth/logout`, { method: 'POST', headers: { Authorization: `Bearer ${token ?? ''}` }, redirect: 'error' }); } catch {}
  };
  const refreshCapabilities = async () => {
    const revision = authRevision.current;
    const generation = client.generation;
    await synchronize();
    if (!client.isCurrent(generation) || revision !== authRevision.current) { const error = new Error('Authentication identity changed'); error.name = 'AbortError'; throw error; }
    const principal = currentUser.current;
    const verified = principal && queryClient.getQueryData<Set<string>>(['my-permissions', principal.id, principal.role, revision]);
    if (!verified) throw new Error('Permissions are unavailable');
    return verified;
  };
  const api = useMemo(() => user ? bindIdentityClient(client) : null, [user?.id, user?.role, privacyEpoch]);
  return <AuthContext.Provider value={{ user, loading, mfaPending, authSignal, privacyEpoch, login, completeMfa, cancelMfa, logout, api, refreshCapabilities }}>
    <Fragment>{children}</Fragment>
  </AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
